if (!customElements.get('sam-video-carousel')) {
  customElements.define('sam-video-carousel', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      this.track = this.querySelector('.sam-real-stories__track');
      this.button = this.querySelector('[data-video-motion]');
      this.videos = [...this.querySelectorAll('video')];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Number(this.dataset.scrollSpeed) || 18;
      this.direction = 1;
      this.userPaused = this.motion.matches;
      this.cooldown = 0;
      this.button.hidden = false;
      on(this.button, 'click', () => {
        this.userPaused = !this.userPaused;
        this.syncButton();
        this.schedule();
      });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovering = true; this.schedule(); } });
      on(this, 'pointerleave', () => { this.hovering = false; this.schedule(); });
      on(this, 'pointerdown', () => { this.interacting = true; this.schedule(); });
      const release = () => {
        if (!this.interacting) return;
        this.interacting = false;
        this.cooldown = performance.now() + 5000;
        this.schedule();
      };
      on(window, 'pointerup', release);
      on(window, 'pointercancel', release);
      on(this, 'wheel', () => { this.cooldown = performance.now() + 5000; this.schedule(); }, { passive: true });
      on(this, 'focusin', () => this.schedule());
      on(this, 'focusout', () => queueMicrotask(() => this.schedule()));
      this.videos.forEach(video => {
        on(video, 'play', () => {
          this.schedule();
          const card = video.closest('.sam-real-stories__column');
          const left = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
          this.track.scrollTo({ left, behavior: this.motion.matches ? 'instant' : 'smooth' });
        });
        ['pause', 'ended'].forEach(event => on(video, event, () => {
          this.cooldown = performance.now() + 2000;
          this.schedule();
        }));
      });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => {
        this.userPaused = this.motion.matches;
        this.syncButton();
        this.schedule();
      });
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= .15;
        this.schedule();
      }, { threshold: [0, .15] });
      this.observer.observe(this.track);
      this.syncButton();
    }
    syncButton() {
      this.button.setAttribute('aria-label', this.userPaused ? 'Resume automatic scrolling' : 'Pause automatic scrolling');
      this.button.querySelector('[data-motion-pause]').hidden = this.userPaused;
      this.button.querySelector('[data-motion-play]').hidden = !this.userPaused;
    }
    canScroll() {
      return this.visible && !this.userPaused && !this.hovering && !this.interacting && !document.hidden && !window.Shopify?.designMode && !this.contains(document.activeElement) && !this.videos.some(video => !video.paused && !video.ended);
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      if (!this.canScroll()) return;
      const wait = this.cooldown - performance.now();
      if (wait > 0) { this.timer = setTimeout(() => this.schedule(), wait); return; }
      this.position = this.track.scrollLeft;
      this.lastTime = 0;
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      if (!this.canScroll()) return;
      const distance = this.track.scrollWidth - this.track.clientWidth;
      const elapsed = this.lastTime ? Math.min(time - this.lastTime, 64) : 0;
      this.lastTime = time;
      if (distance > 2) {
        this.position = Math.max(0, Math.min(distance, this.position + this.direction * this.speed * elapsed / 1000));
        this.track.scrollTo({ left: this.position, behavior: 'instant' });
        // Reverse gently at either end without cloning or interrupting video players.
        if ((this.direction > 0 && this.position >= distance) || (this.direction < 0 && this.position <= 0)) {
          this.direction *= -1;
          this.cooldown = performance.now() + 1600;
          this.schedule();
          return;
        }
      }
      this.frame = requestAnimationFrame(nextTime => this.tick(nextTime));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
    }
  });
}
